import {observer} from 'mobx-react-lite'
import React, {memo, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {ReportMenu} from '@components/columns/report-menu'
import {taskItemFromHub} from '@components/my-tasks/task-item'
import {TaskMenuNew} from '@components/task-menu/task-menu-new'
import {useRowHovered} from '@components/timeline-new/tasks/columns/use-row-hovered'
import {useTimelineNewStore} from '@components/timeline-new/timeline-new-store'
import {ColumnId} from '@components/timeline-new/types'
import {useSyncEventUpdateEffect} from '@containers/hooks'
import {Column} from '@extensions/column'
import {ColumnMenuCell} from './column-menu'
import {TwCell} from './containers'
import {ColumnProps} from './types'

const TwTaskMenu = tw(TaskMenuNew)<{$forceHover?: boolean}>`
  group-hover/row:visible
  ${p => (p.$forceHover ? 'visible' : 'invisible')}
`

export const Menu = memo(({item: {hub, type, path}}: ColumnProps) => {
  if (type === 'task' && hub) {
    return <TaskMenuCell hub={hub} path={path} />
  }

  const reportColumn = hub?.data?.report
  if (reportColumn) {
    return <ReportMenuCell hub={hub} />
  }

  if (type === 'column' && hub && path) {
    const isMirror = Column.isMirror(hub)
    if (!isMirror) {
      return <ColumnMenuCell group={hub} path={path} />
    }
  }

  return null
})

const TaskMenuCell = observer(({hub, path}: {hub: Hub; path: string}) => {
  useSyncEventUpdateEffect('current', {filter: hub.id, debounceTime: 50})

  const ref = useRef<HTMLElement>(null)
  const [, store] = useTimelineNewStore()
  const [opened, setOpened] = useState(false)
  const hovered = useRowHovered(ref)

  const box = Sync.getCurrentSandbox()
  const task = taskItemFromHub(box, hub)
  if (!task) {
    return null
  }

  let content: React.ReactNode
  if (hovered || opened) {
    content = (
      <TwTaskMenu
        $forceHover={opened}
        task={task}
        fromWhere="timeline"
        onToggle={setOpened}
        rename={() => store.startEditing(hub.id, path, ColumnId.title)}
        addSubtask={() => store.startAddingSubtask(path)}
        visibleItems={[
          'add-subtask',
          'complete-task',
          'rename-task',
          'link',
          'delete',
          'archive'
        ]}
      />
    )
  }

  return <TwCell ref={ref}>{content}</TwCell>
})

const ReportMenuCell = observer(({hub}: {hub: Hub}) => {
  useSyncEventUpdateEffect('current', {filter: hub.id, debounceTime: 50})

  const ref = useRef<HTMLElement>(null)

  const [opened, setOpened] = useState(false)
  const hovered = useRowHovered(ref)

  let content: React.ReactNode
  if (hovered || opened) {
    content = <ReportMenu hub={hub} opened={opened} setOpened={setOpened} />
  }

  return <TwCell ref={ref}>{content}</TwCell>
})
